// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

/**
 * Recovery for stale lazy-loaded chunks after a deploy.
 *
 * When a new build is deployed, the hashed asset filenames change. A browser
 * tab that already loaded the previous `index.html` still references the old
 * chunk names, so the next `import()` (React.lazy route) 404s with
 * "Failed to fetch dynamically imported module". A React re-render ("Retry")
 * cannot fix this — the stale URL is baked into the loaded module graph —
 * so the only recovery is a full document reload that re-fetches the current
 * manifest and its chunks.
 *
 * These helpers detect that failure mode and perform a one-time hard reload,
 * guarded so a genuinely-missing chunk can never cause an infinite reload loop.
 */

import { logWarning } from './optional-operation-log.js';

const RELOAD_GUARD_KEY = 'app:chunkReloadAt';
const RELOAD_GUARD_WINDOW_MS = 15_000;

const CHUNK_ERROR_PATTERN =
  /(failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|dynamically imported module|loading chunk \S+ failed|chunkloaderror|failed to import)/i;

/** True when `err` looks like a stale/missing lazy-chunk load failure. */
export function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const message =
    err instanceof Error
      ? `${err.name} ${err.message}`
      : typeof err === 'string'
        ? err
        : String((err as { message?: unknown })?.message ?? '');
  return CHUNK_ERROR_PATTERN.test(message);
}

/**
 * Force a one-time full reload to pick up freshly-deployed assets. Returns
 * `false` (without reloading) if a reload was already triggered within the
 * guard window, so a chunk that is genuinely gone surfaces the error UI
 * instead of reloading forever.
 *
 * IMPORTANT — this app registers a PWA service worker that caches JS/CSS
 * `CacheFirst` (see vite.config.ts). A plain `location.reload()` after a
 * deploy is therefore served the STALE cached `index.html` + chunks by the
 * SW, so it re-lands on the same missing-chunk error and never recovers
 * (only a hard/bypass reload works). To make a soft reload actually fetch
 * the freshly-deployed assets we first drop the SW caches and unregister
 * the worker, then reload. Best-effort: any step may be unavailable, and we
 * reload when the session-storage loop guard was successfully established.
 */
export async function reloadForFreshAssets(): Promise<boolean> {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || '0');
    if (Number.isFinite(last) && Date.now() - last < RELOAD_GUARD_WINDOW_MS) {
      return false;
    }
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // Without a durable guard, automatic reload could repeat on every new page.
    // Return to the existing error UI so the person can explicitly refresh.
    logWarning('assets.reload_guard_unavailable');
    return false;
  }
  await purgeAssetCaches();
  window.location.reload();
  return true;
}

/**
 * Drop the workbox/runtime Cache Storage buckets and unregister the service
 * worker so the next load bypasses the `CacheFirst` static-asset cache and
 * re-fetches the current manifest + chunks from the network. The SW
 * re-registers on the next page load via the PWA runtime. Wrapped in a hard
 * try/catch + short timeout so a hung cache op can never block recovery.
 */
async function purgeAssetCaches(): Promise<void> {
  if (typeof window === 'undefined') return;
  const work = (async () => {
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch {
      // Cache Storage unavailable/blocked — reload will still help once the
      // SW eventually updates.
    }
    try {
      if (navigator.serviceWorker) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch {
      // No SW / unregister blocked — nothing more we can do here.
    }
  })();
  // Never let cleanup stall the recovery reload for more than ~1.5s.
  await Promise.race([work, new Promise<void>((resolve) => setTimeout(resolve, 1500))]);
}

/**
 * Install a global listener for Vite's `vite:preloadError`, which fires when a
 * modulepreload / lazy `import()` fails. Prevents Vite's default (which would
 * otherwise throw) and hard-reloads once to recover from a post-deploy chunk
 * mismatch. Safe to call once at app startup.
 */
export function installChunkReloadHandler(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('vite:preloadError', (event) => {
    // Recover automatically; without this Vite rethrows and the route stays broken.
    event.preventDefault();
    void reloadForFreshAssets();
  });
}
